/**
 * 「场景图」（`product-scene`）的场景设定枚举 —— **唯一真源**。
 *
 * 与 `modelProfile.ts` 同一个理由：这些选项要同时被三处读 ——
 * 页面的选择器、提示词的【场景设定】段、核对页的摘要。写两遍就会分叉，
 * 改了「场景类型」的档位而提示词里还是老的，正是本项目头号 bug 类型，
 * 所以**只有这一份**，谁用谁 import。
 *
 * 2026-09-30 用户口径（`?feature=product-scene` 的配置表）：
 *   场景类型：AI 智能匹配 / 居家 / 办公 / 商业空间 / 户外 / 自定义
 *             └ 「居家」再二级：客厅 卧室 餐厅 厨房 浴室 庭院
 *   场景风格：AI 智能匹配 / 现代 / 极简 / 轻奢 / 自然 / 复古 / 自定义
 *   另外**人相关的全部删掉** —— 这一档只做「产品 + 场景」，不做模特。
 *   要人就走上一档「AI 模特图」，两档的产出目的不同，别混着做。
 *
 * 第一项统一是「AI 智能匹配」= **不指定**，不写进提示词 ——
 * 写「场景类型：AI 智能匹配」给模型看等于没说，只会挤占注意力。
 */

/** 用户选中的那一份场景设定（进 `inputs` 一起提交） */
export type ScenePick = {
  /** 场景类型（见 `SCENE_TYPES` 的 value） */
  type: string
  /** 二级空间，只有给了 `subs` 的类型才有；空串 = 没选 */
  sub: string
  /** 场景风格（见 `SCENE_STYLES`） */
  style: string
}

/** 「没指定」的统一写法 —— 与 modelProfile 的「不限 / 自动」等价 */
export const SCENE_AUTO = 'AI 智能匹配'
/** 选它表示「我自己在场景描述里写」 */
export const SCENE_CUSTOM = '自定义'

export type SceneType = {
  value: string
  label: string
  /** 有它这个类型才渲染二级选择器 —— 二级是「这个类型下的具体空间」 */
  subs?: string[]
}

/** 顺序 = 页面渲染顺序 = 提示词里的书写顺序 */
export const SCENE_TYPES: SceneType[] = [
  { value: SCENE_AUTO, label: SCENE_AUTO },
  { value: '居家', label: '居家', subs: ['客厅', '卧室', '餐厅', '厨房', '浴室', '庭院'] },
  { value: '办公', label: '办公' },
  { value: '商业空间', label: '商业空间' },
  { value: '户外', label: '户外' },
  { value: SCENE_CUSTOM, label: SCENE_CUSTOM },
]

export const SCENE_STYLES = [SCENE_AUTO, '现代', '极简', '轻奢', '自然', '复古', SCENE_CUSTOM]

export const SCENE_DEFAULT: ScenePick = { type: SCENE_AUTO, sub: '', style: SCENE_AUTO }

/** 这一项算不算「用户指定了」—— 「AI 智能匹配」是没指定 */
export const isSceneSet = (v: string) => Boolean(v) && v !== SCENE_AUTO

/** 某个场景类型下的二级空间（没有二级时返回空数组，页面据此决定渲不渲染） */
export const subsOf = (type: string): string[] =>
  SCENE_TYPES.find((t) => t.value === type)?.subs ?? []

/**
 * 把选择拼成一段给模型看的话。全「AI 智能匹配」时返回空串 ——
 * 调用方据此决定要不要在提示词里输出【场景设定】这一段（空段比
 * 「场景类型：AI 智能匹配」更干净）。
 *
 * 选「自定义」时必须告诉模型**去哪找答案**（场景描述那一栏），
 * 否则「场景风格：自定义」这五个字对模型是零信息。
 */
export function sceneText(s: ScenePick): string {
  const parts: string[] = []
  if (isSceneSet(s.type)) {
    parts.push(
      s.type === SCENE_CUSTOM
        ? '场景类型：自定义（以场景描述为准）'
        : `场景类型：${s.type}${s.sub ? ` · ${s.sub}` : ''}`,
    )
  }
  if (isSceneSet(s.style)) {
    parts.push(s.style === SCENE_CUSTOM ? '场景风格：自定义（以场景描述为准）' : `场景风格：${s.style}`)
  }
  return parts.join('；')
}
